import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowUpRight, ArrowLeft, Check, ChevronDown } from "lucide-react";
import { motion } from "framer-motion";
import { seoKeywords, getSeoBySlug, groupedSeo, seoCategories, seoCategoriesFr } from "@/data/seoKeywords";
import { services, getServiceBySlug } from "@/data/services";
import { cities, getCityBySlug } from "@/data/cities";
import { useLang } from "@/context/LanguageContext";
import { hreflangLinks, localizedPath, isFrPath, enTwinFor, SITE_BASE_URL } from "@/i18n/paths";
import { useLocation } from "react-router-dom";
import { FadeIn, MaskedReveal } from "@/components/Reveal";
import { JsonLd, breadcrumbSchema } from "@/components/JsonLd";

// ---- Index / hub page ----
export const SeoIndex = () => {
  const grouped = groupedSeo();
  const [q, setQ] = React.useState("");
  const { t, lang } = useLang();
  const { pathname } = useLocation();
  const enPath = enTwinFor(pathname);
  const fr = lang === "fr";
  const cats = fr ? seoCategoriesFr : seoCategories;
  const filter = (list) => list.filter((k) => k.keyword.toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      <Helmet>
        <title>{t("meta.seo_index_title")}</title>
        <meta name="description" content={t("meta.seo_index_desc")} />
        <meta name="keywords" content="security guard services Quebec, security keywords Quebec, security industry Canada, security services directory, agence de sécurité Québec" />
        <link rel="canonical" href={`${SITE_BASE_URL}${fr ? pathname : enPath}`} />
        {(hreflangLinks(enPath) || []).map((l) => (
          <link key={l.hrefLang} rel={l.rel} hrefLang={l.hrefLang} href={l.href} />
        ))}
      </Helmet>

      <section className="px-6 md:px-10 pt-24 md:pt-32 pb-16 max-w-[1600px] mx-auto">
        <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 flex items-center gap-3 mb-6">
          <span className="w-8 h-px bg-amber-500" />
          {fr ? `Répertoire de couverture · ${seoKeywords.length} pages` : `Coverage Directory · ${seoKeywords.length} pages`}
        </div>
        <h1 className="font-display uppercase font-black text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.9] text-white max-w-5xl">
          <MaskedReveal>
            <span>{fr ? "Chaque requête." : "Every search term."}</span>
            <span className="text-amber-500">{fr ? "Chaque opération." : "Every operation."}</span>
          </MaskedReveal>
        </h1>
        <FadeIn delay={0.2}>
          <p className="mt-8 text-zinc-400 text-lg max-w-2xl">
            {fr
              ? `${seoKeywords.length} pages dédiées couvrant le spectre des recherches de l'industrie canadienne de la sécurité — service × ville, intention et secteur d'activité. Trouvez votre besoin exact ci-dessous.`
              : `${seoKeywords.length} dedicated landing pages covering the Canadian security industry search spectrum — service × city, intent, and industry vertical. Find your exact operating need below.`}
          </p>
        </FadeIn>

        <FadeIn delay={0.25}>
          <div className="mt-12 relative max-w-xl">
            <input
              data-testid="seo-filter-input"
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={fr ? "Rechercher parmi les pages…" : "Search 110+ keyword pages…"}
              className="w-full bg-transparent border border-white/15 focus:border-amber-500 h-14 px-5 text-base text-white placeholder:text-zinc-600 focus:outline-none transition-colors"
            />
          </div>
        </FadeIn>
      </section>

      <section className="px-6 md:px-10 pb-24 max-w-[1600px] mx-auto">
        {Object.entries(grouped).map(([catKey, list]) => {
          const filtered = filter(list);
          if (filtered.length === 0) return null;
          const cat = cats[catKey];
          if (!cat) return null;
          return (
            <div key={catKey} className="mb-20">
              <div className="border-b border-white/10 pb-4 mb-8 flex items-baseline justify-between">
                <div>
                  <h2 className="font-display uppercase font-black text-3xl md:text-4xl tracking-tight text-white">{cat.label}</h2>
                  <p className="mt-2 text-zinc-400 text-sm">{cat.description}</p>
                </div>
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500 shrink-0 ml-4">
                  {filtered.length} pages
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {filtered.map((k) => (
                  <Link
                    key={k.slug}
                    to={localizedPath(`/seo/${k.slug}`, lang)}
                    data-testid={`seo-card-${k.slug}`}
                    className="group flex items-start justify-between border border-white/10 hover:border-amber-500 hover:bg-amber-500 p-5 transition-colors duration-300"
                  >
                    <div className="font-display uppercase text-sm md:text-base tracking-tight text-white group-hover:text-black leading-tight">
                      {k.keyword}
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-zinc-600 group-hover:text-black shrink-0 ml-3 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      </section>
    </>
  );
};

// ---- FAQ component ----
const FaqItem = ({ q, a, defaultOpen = false }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="border-b border-white/10">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between gap-6 py-6 text-left group">
        <span className="font-display uppercase text-lg md:text-2xl tracking-tight text-white group-hover:text-amber-500 transition-colors">{q}</span>
        <ChevronDown className={`w-5 h-5 text-amber-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        className="overflow-hidden"
      >
        <p className="pb-6 pr-10 text-zinc-400 text-base leading-relaxed">{a}</p>
      </motion.div>
    </div>
  );
};

// ---- Per-keyword landing page ----
export const SeoLanding = () => {
  const { slug } = useParams();
  const k = getSeoBySlug(slug);
  const { t, lang } = useLang();
  const { pathname } = useLocation();
  const fr = lang === "fr";
  const lp = (p) => localizedPath(p, lang);
  const cats = fr ? seoCategoriesFr : seoCategories;

  // Category-specific content (hooks must run unconditionally; guard body below)
  const service = k?.service_slug ? getServiceBySlug(k.service_slug) : null;
  const city = k?.city_slug ? getCityBySlug(k.city_slug) : null;
  const heroImage =
    service?.image ||
    (city ? "https://images.unsplash.com/photo-1519178614-68673b201f36?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000" : "https://images.unsplash.com/photo-1517913451214-e22ce660e086?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000");

  // Body content variants
  const bodyParagraphs = React.useMemo(() => {
    if (!k) return [];
    if (fr) {
      const svcName = service ? service.name_fr.toLowerCase() : "";
      const svcLong = service ? (service.long_fr || service.long_en) : "";
      if (k.category === "service-city" && service && city) {
        return [
          `« ${k.keyword} » est un service offert par Quebec Security Services sous un permis du Bureau de la sécurité privée (BSP), avec des agents bilingues français/anglais et un centre de commandement québécois 24/7. Qu'il s'agisse d'une tour corporative au centre-ville de ${city.name}, d'un commerce du secteur ${city.landmarks?.split(",")[0] || city.name} ou d'un site industriel en périphérie de ${city.name}, notre opération de ${svcName} est planifiée à partir d'une visite réelle des lieux — jamais d'une grille tarifaire générique.`,
          svcLong,
          `À ${city.name}, notre déploiement reflète le profil local : les secteurs ${city.industries?.slice(0, 3).join(", ") || "corporatif, industriel et résidentiel"} dominent notre portefeuille, et nos agents sont formés aux codes du bâtiment, aux attentes des locataires et au cadre réglementaire (CNESST, Loi 25) qui s'applique dans la région ${city.region}. Le temps médian vérifié d'intervention sur alarme à ${city.name} est de moins de 12 minutes.`,
          `Chaque contrat de ${svcName} à ${city.name} comprend : des agents licenciés BSP (vérifiables dans notre portail client), une couverture bilingue FR/EN au niveau CECR B2 minimum, plus de 60 heures de formation pré-déploiement, des rapports d'activité quotidiens de calibre assurance et une responsabilité civile commerciale de 10 M$ avec avenant d'assuré additionnel sur demande.`,
        ];
      }
      if (k.category === "auto-city" && city) {
        return [
          `« ${k.keyword} » est une requête que les entreprises québécoises utilisent lorsqu'elles cherchent des agents de sécurité licenciés BSP et bilingues à ${city.name}. Quebec Security Services livre exactement cela — agents en uniforme sur poste, patrouilles mobiles, intervention sur alarme et couverture spécialisée adaptée au contexte opérationnel de ${city.name} et de la région ${city.region}.`,
          `Notre déploiement à ${city.name} couvre ${city.landmarks || `chaque secteur de ${city.name}`}. Le portefeuille reflète l'économie locale : les organisations des secteurs ${city.industries?.slice(0, 3).join(", ") || "corporatif, industriel et résidentiel"} sont au cœur de nos mandats à ${city.name}, et chaque agent complète une formation propre au site — quartiers, attentes des locataires, cadre réglementaire (CNESST, Loi 25, BSP).`,
          `Quand vous cherchez « ${k.keyword.toLowerCase()} », vous cherchez une firme qui traite ${city.name} comme un environnement opérationnel précis — pas un modèle copié d'un playbook de Toronto ou de Vancouver. Chaque agent de notre équipe de ${city.name} est bilingue français/anglais au niveau CECR B2 minimum. Chaque incident est consigné dans notre portail client. Chaque obligation réglementaire est documentée au niveau exigé par les assureurs.`,
          `Temps médian vérifié d'intervention sur alarme à ${city.name} : moins de 12 minutes depuis notre centre de commandement québécois. Déploiement en moins de 45 minutes depuis nos points stratégiques de Montréal, Laval, Québec, Sherbrooke, Trois-Rivières, Gatineau, Val-d'Or et Sept-Îles. Responsabilité civile de 10 M$, responsabilité professionnelle et couverture CNESST. Certificat d'assurance émis à la signature du contrat.`,
        ];
      }
      if (k.category === "auto-service" && service) {
        return [
          `« ${k.keyword} » est une requête qui mène les clients québécois exactement vers la bonne page de service. Notre division de ${svcName} est licenciée BSP, bilingue et déployée dans chaque municipalité constituée du Québec.`,
          svcLong,
          `Que votre site soit au centre-ville de Montréal, dans un parc industriel de Laval, sur un campus institutionnel de Québec, dans une installation fédérale de Gatineau ou sur un site minier du corridor de l'Abitibi, notre opération de ${svcName} est planifiée à partir d'une visite des lieux — jamais d'une grille tarifaire générique. Chaque agent complète plus de 60 heures de formation pré-déploiement en plus du socle BSP.`,
          `Chaque contrat de ${svcName} comprend : agents licenciés BSP (vérifiables via le portail client), couverture bilingue FR/EN, rapports d'activité quotidiens de calibre assurance, intervention sur alarme en moins de 12 minutes depuis notre centre de commandement québécois, et conformité complète CNESST + Loi 25. Certificat d'assurance et avenants d'assuré additionnel disponibles sur demande.`,
        ];
      }
      if (k.category === "intent") {
        return [
          `« ${k.h1} » signifie une chose chez Quebec Security Services : des agents licenciés BSP, bilingues et prêts pour l'assureur, déployés sur votre site avec la discipline que votre contexte exige. Nous ne dotons pas nos postes depuis un bassin de sous-traitance. Chaque agent est notre employé, formé selon notre protocole et vérifiable dans notre portail client dès les 60 premières secondes d'un quart.`,
          `Notre approche est ancrée dans le cadre réglementaire québécois. Chaque agent détient une licence active du Bureau de la sécurité privée (BSP) et complète plus de 60 heures de formation pré-déploiement : conformité CNESST, Loi 25 sur les renseignements personnels, désescalade verbale, premiers soins RCR-C, protocole radio et formation propre au site.`,
          `Nous déployons à Montréal, Québec, Laval, Gatineau, Longueuil, Sherbrooke, Trois-Rivières, Saguenay, Val-d'Or, Rouyn-Noranda, Sept-Îles, Fermont et chaque municipalité constituée du Québec. Déploiement en moins de 45 minutes dans 17 régions, depuis nos points stratégiques incluant notre siège de Montréal et nos équipes de réponse rapide de Québec, Val-d'Or et Sept-Îles.`,
          `Chaque contrat comprend une documentation de calibre assurance via notre portail client — rapports d'activité quotidiens, rapports d'incidents horodatés, registres de licences et dossiers de formation. Certificat d'assurance émis à la signature et renouvelé automatiquement. Responsabilité civile commerciale de 10 M$, responsabilité professionnelle et indemnisation des travailleurs.`,
        ];
      }
      // vertical
      return [
        `« ${k.h1} » est une discipline de sécurité spécialisée que Quebec Security Services livre sous un permis BSP avec des agents bilingues FR/EN. Le cadre réglementaire, les exigences d'assurance, le profil d'incidents et le modèle de dotation propres à l'industrie façonnent chaque déploiement.`,
        `Notre approche de ce secteur commence par une visite des lieux menée par un consultant en sécurité : contexte opérationnel documenté, obligations CNESST cartographiées, conformité Loi 25, et un plan opérationnel que vous pouvez remettre directement à votre assureur, votre conseil d'administration et votre régulateur.`,
        `La sélection des agents compte davantage dans les déploiements sectoriels qu'en sécurité commerciale générale. Chaque agent affecté à votre compte est jumelé au profil de votre industrie — formation, langue, tempérament et certifications requises. La formation propre au site varie de 8 à 40 heures selon la complexité.`,
        `Rapports et préparation aux audits sont intégrés dès le premier quart : chaque incident est documenté, chaque quart génère un rapport d'activité, chaque obligation réglementaire est accompagnée de documents accessibles en temps réel via votre portail client. Certificat d'assurance émis à la signature; avenants d'assuré additionnel disponibles pour propriétaires, entrepreneurs généraux et organismes de réglementation.`,
      ];
    }
    if (k.category === "service-city" && service && city) {
      return [
        `${k.keyword} is delivered by Quebec Security Services under a Bureau de la sécurité privée (BSP) permit, with bilingual French/English officers and a 24/7 Quebec Command Centre. Whether the operating context is a downtown ${city.name} corporate tower, a ${city.landmarks?.split(",")[0] || city.name} district retail plaza, or an industrial site on the outskirts of ${city.name}, our ${service.name_en.toLowerCase()} operation is scoped from a physical site survey — never a blanket rate card.`,
        `${service.long_en.substring(0, 400)}`,
        `In ${city.name} specifically, our deployment reflects the local operating profile: ${city.industries?.slice(0, 3).join(", ") || "corporate, industrial and residential"} clients dominate the client mix, and our officers are trained on the specific building codes, tenant expectations, and regulatory overlay (CNESST, Loi 25) that apply in the ${city.region} region. Median verified alarm response across ${city.name} is under 12 minutes from our Quebec Command Centre.`,
        `Every ${service.name_en.toLowerCase()} contract in ${city.name} includes: BSP-licensed officers (licenses verifiable in our client portal), bilingual FR/EN coverage to CEFR B2 minimum, 60+ hours of pre-deployment training plus site-specific onboarding, insurance-grade daily activity reports, and a $10M commercial general liability policy with additional-insured endorsement available on request.`,
      ];
    }
    if (k.category === "auto-city" && city) {
      return [
        `${k.keyword} is a search phrase Quebec businesses use when they need BSP-licensed, bilingual FR/EN security guards in ${city.name}. Quebec Security Services delivers exactly that — uniformed officers on-post, mobile patrol swings, alarm response, and specialized coverage tailored to the operating context of ${city.name} and the surrounding ${city.region} region.`,
        `Our ${city.name} deployment covers ${city.landmarks || `every district of ${city.name}`}. The client mix reflects the local economy: ${city.industries?.slice(0, 3).join(", ") || "corporate, industrial and residential"} operators anchor our roster in ${city.name}, and every officer completes site-specific onboarding on the neighbourhoods, tenant expectations, and regulatory overlay (CNESST, Loi 25, BSP) that apply here.`,
        `When you search for "${k.keyword.toLowerCase()}" you are looking for a security firm that treats ${city.name} as a specific operating environment — not a copy-paste template from a Toronto or Vancouver playbook. Every officer on our ${city.name} roster is bilingual French/English to CEFR B2 minimum. Every incident is logged in our client portal. Every regulatory obligation is met with insurance-grade documentation.`,
        `Median verified alarm response in ${city.name} is under 12 minutes from our Quebec Command Centre. Sub-45-minute deployment reach from strategic staging points in Montreal, Laval, Quebec City, Sherbrooke, Trois-Rivières, Gatineau, Val-d'Or and Sept-Îles. $10M commercial general liability, professional liability, and CNESST workers' compensation. Certificate of insurance issued at contract execution.`,
      ];
    }
    if (k.category === "auto-service" && service) {
      return [
        `${k.keyword} is a search phrase that lands prospective Quebec clients on exactly the right service page. Our ${service.name_en.toLowerCase()} division is BSP-licensed, bilingual, and deployed across every incorporated municipality in Quebec.`,
        `${service.long_en.substring(0, 500)}`,
        `Whether your operating context sits in downtown Montreal, a Laval industrial park, a Quebec City institutional campus, a Gatineau federal facility, or a mining site in the Abitibi corridor, our ${service.name_en.toLowerCase()} operation is scoped from an on-site walk-through — never a blanket rate card. Every officer completes 60+ hours of pre-deployment training on top of BSP baseline.`,
        `Every ${service.name_en.toLowerCase()} contract includes: BSP-licensed officers (verifiable via client portal), bilingual FR/EN coverage, insurance-grade daily activity reports, sub-12-minute alarm response from our Quebec Command Centre, and full CNESST + Loi 25 regulatory alignment. Certificate of insurance and additional-insured endorsements available on request.`,
      ];
    }
    if (k.category === "intent") {
      return [
        `${k.h1} means one thing at Quebec Security Services: BSP-licensed, bilingual, insurer-ready officers deployed to your site with the discipline the operating context requires. ${k.hook} We do not staff from a broker's overflow pool. We do not sub-tier the contract three layers deep. Every officer on the roster is our employee, trained under our protocol, and verifiable through our client portal in the first 60 seconds of a shift.`,
        `Our approach to ${k.keyword.toLowerCase()} is grounded in the Quebec regulatory environment. Every officer holds an active Bureau de la sécurité privée (BSP) license. Every officer completes 60+ hours of pre-deployment training including CNESST workplace-safety compliance, Loi 25 personal-information handling, verbal de-escalation, first-aid CPR-C, radio protocol, and site-specific onboarding tailored to the operating context.`,
        `We deploy ${k.keyword.toLowerCase()} across Montreal, Quebec City, Laval, Gatineau, Longueuil, Sherbrooke, Trois-Rivières, Saguenay, Val-d'Or, Rouyn-Noranda, Sept-Îles, Fermont and every incorporated municipality in Quebec. Sub-45-minute deployment reach across 17 regions, staged from strategic points including our Montreal HQ, Quebec City rapid-response bench, and mining-corridor staging in Val-d'Or and Sept-Îles.`,
        `Every contract includes insurance-grade documentation via our client portal — daily activity reports (DARs), time-stamped incident reports, license verification logs, training records, and CNESST-aligned audit packages. Certificate of insurance issued at contract execution and automatically renewed annually. $10M commercial general liability, professional liability, and workers' compensation.`,
      ];
    }
    // vertical
    return [
      `${k.h1} is a specialized security discipline that Quebec Security Services delivers under a BSP permit with bilingual FR/EN officers. ${k.hook} The industry-specific operating patterns — regulatory overlay, insurance requirements, incident profile, staffing model — all shape the deployment.`,
      `Our approach to this vertical starts with an on-site walk-through by a security consultant who documents your specific operating context, maps CNESST workplace-safety obligations, aligns the operation with Loi 25 personal-information handling, and produces a scoped operations plan you can hand directly to your insurer, your board, and your regulator.`,
      `Officer selection matters more in industry-vertical deployments than in general commercial security. Every officer assigned to your account is matched to your vertical's operating profile — the training, language, temperament, and specific certifications your industry requires. Site-specific onboarding runs from 8 to 40 hours depending on complexity.`,
      `Reporting and audit-readiness are built into the operation from shift one. Every incident is documented, every shift generates a DAR, every regulatory obligation is met with paperwork available in real time through your client portal. Certificate of insurance is issued at contract execution, and additional-insured endorsements are available for landlords, GCs, and regulatory bodies.`,
    ];
  }, [k, service, city, fr]);

  const relatedServices = React.useMemo(() => {
    if (!k) return [];
    if (k.category === "service-city" && service) {
      return services.filter((s) => s.slug !== service.slug).slice(0, 4);
    }
    return services.slice(0, 4);
  }, [k, service]);

  const relatedCities = React.useMemo(() => {
    if (!k) return [];
    if (k.category === "service-city" && city) {
      return cities.filter((c) => c.slug !== city.slug).slice(0, 12);
    }
    return cities.slice(0, 12);
  }, [k, city]);

  const faqs = React.useMemo(() => {
    if (!k) return [];
    const kw = k.keyword;
    const kwLower = kw.toLowerCase();
    if (fr) {
      return [
        {
          q: `Ce service (« ${kw} ») est-il offert partout au Québec?`,
          a: `Oui. Ce service est livré par Quebec Security Services dans chaque municipalité constituée du Québec — Montréal, Québec, Laval, Gatineau, Longueuil, Sherbrooke, Trois-Rivières, Saguenay, Val-d'Or, Rouyn-Noranda, Sept-Îles, Fermont et toutes les régions entre les deux. Déploiement en moins de 45 minutes partout en province.`,
        },
        {
          q: `Vos agents sont-ils licenciés pour ce type de mandat?`,
          a: `Chaque agent déployé détient une licence active du Bureau de la sécurité privée (BSP) — catégorie A pour les mandats non armés, catégorie C pour les mandats armés lorsqu'applicable. Les cartes de licence sont enregistrées dans notre portail client et peuvent être produites sur demande pour un audit ou une vérification d'assureur.`,
        },
        {
          q: `Quelle est votre rapidité de déploiement?`,
          a: `Les déploiements standards se lancent en 48 à 72 heures après la signature du contrat. Les déploiements d'urgence — post-incident, présence préventive en cas de violence au travail, couverture immédiate — sont disponibles le jour même depuis nos équipes de réponse rapide de Montréal et de Québec.`,
        },
        {
          q: `Combien coûte ce service au Québec?`,
          a: `Le prix est établi à partir d'une visite réelle des lieux — nous ne publions pas de grille tarifaire qui ignore votre contexte. Les taux typiques pour des agents non armés au Québec varient entre 28 $ et 42 $ l'heure selon la structure des quarts, le profil de risque du site et la géographie. Chaque soumission est transparente, détaillée et livrée en un jour ouvrable.`,
        },
        {
          q: `Offrez-vous des agents bilingues?`,
          a: `Chaque agent est bilingue français/anglais au niveau CECR B2 minimum. Les agents sur les postes en contact avec les locataires (conciergerie, réception corporative, hôtellerie) sont tenus au niveau C1. Vos employés, locataires et invités sont servis dans la langue de leur choix.`,
        },
        {
          q: `Êtes-vous assurés et cautionnés?`,
          a: `Quebec Security Services détient une responsabilité civile commerciale de 10 M$, une responsabilité professionnelle et une couverture CNESST. Un certificat d'assurance est émis à la signature du contrat et renouvelé automatiquement. Des avenants d'assuré additionnel sont disponibles pour propriétaires, entrepreneurs généraux et producteurs d'événements.`,
        },
      ];
    }
    return [
      {
        q: `Is ${kw} available across Quebec?`,
        a: `Yes. ${kw} is delivered by Quebec Security Services across every incorporated municipality in Quebec — Montreal, Quebec City, Laval, Gatineau, Longueuil, Sherbrooke, Trois-Rivières, Saguenay, Val-d'Or, Rouyn-Noranda, Sept-Îles, Fermont and every region in between. Sub-45-minute deployment reach province-wide.`,
      },
      {
        q: `Are your officers licensed for ${kwLower}?`,
        a: `Every officer deployed on ${kwLower} contracts holds an active Bureau de la sécurité privée (BSP) license — Category A for unarmed, Category C for armed where applicable. License cards are recorded in our client portal and produceable on-demand for audit or insurer verification.`,
      },
      {
        q: `How quickly can you deploy ${kwLower}?`,
        a: `Standard deployments launch within 48–72 hours of contract signature. Emergency ${kwLower} deployments — post-incident, workplace-violence standby, immediate coverage — are available same-day from our rapid-response benches in Montreal and Quebec City.`,
      },
      {
        q: `How much does ${kwLower} cost in Quebec?`,
        a: `Pricing for ${kwLower} is scoped from a physical or documented site survey — we do not publish a rate card that ignores your operating context. Typical unarmed rates in Quebec fall between $28 and $42 per hour depending on shift structure, site risk profile, and geographic staging. Every quote is transparent, itemized, and delivered within one business day.`,
      },
      {
        q: `Do you provide bilingual officers for ${kwLower}?`,
        a: `Every officer is bilingual French/English to CEFR B2 minimum. Officers on tenant-facing posts (concierge, corporate reception, hotel) are held to CEFR C1. Your Quebec workforce, tenants, and guests are served in the language they prefer.`,
      },
      {
        q: `Is ${kwLower} insured and bonded?`,
        a: `Quebec Security Services carries $10M commercial general liability, professional liability, and CNESST workers' compensation coverage. A certificate of insurance is issued at contract execution and renewed automatically at policy anniversary. Additional-insured endorsements are available for landlords, GCs, and event producers.`,
      },
    ];
  }, [k, fr]);

  if (!k) return <Navigate to={fr ? "/fr/seo" : "/seo"} replace />;

  const enPath = enTwinFor(pathname);
  const pageTitle = fr ? `${k.h1} | Maze Security` : `${k.h1} | Maze Security`;
  const pageDesc = fr
    ? `« ${k.keyword} » — Licencié BSP, bilingue FR/EN${city ? `, à ${city.name}` : ""}. Déployé partout au Québec par Maze Security. Soumission en un jour ouvrable — appelez le (807) 907-2874.`
    : k.description;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta name="keywords" content={(k.keywords || [k.keyword]).join(", ")} />
        <link rel="canonical" href={`${SITE_BASE_URL}${fr ? pathname : enPath}`} />
        {(hreflangLinks(enPath) || []).map((l) => (
          <link key={l.hrefLang} rel={l.rel} hrefLang={l.hrefLang} href={l.href} />
        ))}
      </Helmet>
      <JsonLd data={breadcrumbSchema([
        { name: fr ? "Accueil" : "Home", path: fr ? "/fr" : "/" },
        { name: fr ? "Répertoire" : "Coverage Directory", path: fr ? "/fr/seo" : "/seo" },
        { name: k.keyword, path: `/seo/${k.slug}` },
      ])} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }} />

      {/* Hero */}
      <section className="relative min-h-[60vh] w-full overflow-hidden bg-black flex items-end">
        <img src={heroImage} alt={k.keyword} className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        <div className="relative z-10 max-w-[1600px] mx-auto w-full px-6 md:px-10 py-20">
          <Link to={fr ? "/fr/seo" : "/seo"} className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400 hover:text-amber-500 transition-colors mb-8">
            <ArrowLeft className="w-3 h-3" /> {fr ? "Répertoire" : "Directory"}
          </Link>
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 flex items-center gap-3 mb-8">
            <span className="w-8 h-px bg-amber-500" />
            {cats[k.category]?.label || "Coverage"}
          </div>
          <h1
            data-testid="seo-landing-h1"
            className="font-display uppercase font-black text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[0.95] text-white max-w-5xl"
          >
            <MaskedReveal>
              {k.h1.split(" ").map((w, i) => <span key={i}>{w}&nbsp;</span>)}
            </MaskedReveal>
          </h1>
        </div>
      </section>

      {/* Body */}
      <section className="px-6 md:px-10 py-20 max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-8">
          <FadeIn>
            <p className="text-xl md:text-2xl text-white leading-relaxed">{k.description}</p>
          </FadeIn>
          <div className="mt-10 space-y-6">
            {bodyParagraphs.map((p, i) => (
              <FadeIn key={i} delay={i * 0.05}>
                <p className="text-base md:text-lg text-zinc-400 leading-relaxed">{p}</p>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={0.3}>
            <div className="mt-14">
              <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-zinc-500 mb-4">→ {fr ? "Ce qui est inclus" : "What's included"}</div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(fr
                  ? ["Agents licenciés BSP", "Bilingues FR/EN", "60+ heures de formation pré-déploiement", "Centre de commandement québécois 24/7", "Rapports quotidiens de calibre assurance", "Intervention sur alarme en moins de 12 minutes"]
                  : ["BSP-licensed officers", "Bilingual FR/EN", "60+ hours pre-deployment training", "24/7 Quebec Command Centre", "Insurance-grade DAR reporting", "Sub-12 minute alarm response"]
                ).map((b, i) => (
                  <li key={i} className="flex items-center gap-3 text-white text-sm">
                    <span className="w-6 h-6 bg-amber-500 text-black flex items-center justify-center">
                      <Check className="w-3.5 h-3.5" />
                    </span>
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </FadeIn>

          <FadeIn delay={0.35}>
            <div className="mt-14">
              <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-zinc-500 mb-4">→ {fr ? "Termes de recherche connexes" : "Related search terms"}</div>
              <div className="flex flex-wrap gap-2">
                {(k.keywords || [k.keyword]).map((kw) => (
                  <span key={kw} className="border border-white/15 px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.15em] text-zinc-400">
                    {kw}
                  </span>
                ))}
              </div>
            </div>
          </FadeIn>
        </div>

        <aside className="md:col-span-4 md:pl-8 md:border-l md:border-white/10">
          <FadeIn>
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 mb-4">→ {fr ? "Déployer" : "Deploy"}</div>
            <h3 className="font-display uppercase font-black text-3xl tracking-tight leading-none text-white">
              {fr ? (<>Parlez-nous<br />en.</>) : (<>Talk<br />to us.</>)}
            </h3>
            <p className="mt-4 text-zinc-400 text-sm">{fr ? "Réponse en un jour ouvrable. Sans démarcheurs." : "One business day reply. No phone tag."}</p>
            <Link to={lp("/contact")} data-testid="seo-landing-cta" className="mt-6 group inline-flex items-center justify-between gap-4 bg-amber-500 hover:bg-amber-400 text-black h-12 px-5 w-full font-mono text-[11px] uppercase tracking-[0.2em] transition-colors">
              {t("common.request_quote")}
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </FadeIn>

          {service && (
            <FadeIn delay={0.1}>
              <div className="mt-10 border-t border-white/10 pt-8">
                <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-zinc-500 mb-4">{fr ? "Page complète du service" : "Full service page"}</div>
                <Link to={lp(`/services/${service.slug}`)} className="group flex items-center justify-between border border-white/10 hover:border-amber-500 p-4 transition-colors">
                  <span className="font-display uppercase text-base tracking-tight text-white group-hover:text-amber-500">{fr ? service.name_fr : service.name_en}</span>
                  <ArrowUpRight className="w-3.5 h-3.5 text-amber-500" />
                </Link>
              </div>
            </FadeIn>
          )}

          {city && (
            <FadeIn delay={0.15}>
              <div className="mt-6">
                <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-zinc-500 mb-4">{fr ? "Page complète de la ville" : "Full city page"}</div>
                <Link to={lp(`/cities/${city.slug}`)} className="group flex items-center justify-between border border-white/10 hover:border-amber-500 p-4 transition-colors">
                  <span className="font-display uppercase text-base tracking-tight text-white group-hover:text-amber-500">{city.name}</span>
                  <ArrowUpRight className="w-3.5 h-3.5 text-amber-500" />
                </Link>
              </div>
            </FadeIn>
          )}
        </aside>
      </section>

      {/* FAQ */}
      <section className="border-t border-white/10 px-6 md:px-10 py-24 max-w-[1600px] mx-auto">
        <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 mb-6">→ FAQ</div>
        <h2 className="font-display uppercase font-black text-4xl md:text-6xl tracking-tight leading-[0.9] text-white max-w-4xl">
          {fr ? `${k.keyword} — vos questions, nos réponses.` : `${k.keyword} — questions answered.`}
        </h2>
        <div className="mt-14 max-w-4xl">
          {faqs.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} defaultOpen={i === 0} />)}
        </div>
      </section>

      {/* Related services */}
      <section className="border-t border-white/10 px-6 md:px-10 py-20 max-w-[1600px] mx-auto">
        <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 mb-6">→ {fr ? "Services connexes" : "Related services"}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {relatedServices.map((s) => (
            <Link key={s.slug} to={lp(`/services/${s.slug}`)} className="group border border-white/10 hover:border-amber-500 p-5 transition-colors">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 group-hover:text-amber-500">/ {s.number}</div>
              <div className="mt-2 font-display uppercase text-base tracking-tight text-white group-hover:text-amber-500 leading-tight">{fr ? s.name_fr : s.name_en}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* Related cities */}
      <section className="border-t border-white/10 px-6 md:px-10 py-20 max-w-[1600px] mx-auto">
        <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 mb-6">→ {fr ? "Villes desservies" : "Available in these cities"}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {relatedCities.map((c) => (
            <Link key={c.slug} to={lp(`/cities/${c.slug}`)} className="group flex items-center justify-between border border-white/10 hover:border-amber-500 hover:bg-amber-500 px-4 py-3 transition-colors">
              <span className="font-display uppercase text-sm tracking-tight text-white group-hover:text-black">{c.name}</span>
              <ArrowUpRight className="w-3 h-3 text-zinc-600 group-hover:text-black" />
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-amber-500 text-black">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] mb-4">→ {k.keyword}</div>
              <h3 className="font-display uppercase font-black text-4xl md:text-6xl tracking-tight leading-[0.9]">{fr ? (<>Déployez au Québec.<br />Parlez-nous.</>) : (<>Deploy in Quebec.<br />Talk to us.</>)}</h3>
            </div>
            <Link to={lp("/contact")} className="group inline-flex items-center gap-4 bg-black text-amber-500 h-14 px-6 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-zinc-900 transition-colors self-start">
              {t("common.request_quote")}
              <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};
